import { useState } from 'react';

interface CartItem {
  sku: string;
  title: string;
  price: number;
  brand?: string;
  size?: string;
  photoUrl?: string;
  siteName: string;
  siteId: number;
}

interface TaxLineDisplay {
  label: string;
  amount: number;
}

interface CompletedOrder {
  items: CartItem[];
  subtotal: number;
  taxLines: TaxLineDisplay[];
  shippingMethod: 'pickup' | 'canadapost' | 'ups';
  shippingCost: number;
  total: number;
  completedAt: number;
  orderNumber?: string;
  squareOrderId?: string;
  paymentId?: string;
  receiptToken?: string;
  customerName?: string;
  customerEmail?: string;
  inventoryMode?: 'live' | 'simulated';
  isTest?: boolean;
}

interface OrderConfirmationProps {
  order: CompletedOrder;
  onContinueShopping: () => void;
}

const API_BASE =
  import.meta.env.VITE_API_URL || 'https://cc-api.viconsignment.ca/api/v1';

export default function OrderConfirmation({ order, onContinueShopping }: OrderConfirmationProps) {
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownloadReceipt = async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      const params = new URLSearchParams();
      if (order.squareOrderId) params.set('squareOrderId', order.squareOrderId);
      if (order.orderNumber) params.set('orderNumber', order.orderNumber);
      if (order.receiptToken) params.set('token', order.receiptToken);

      const headers: Record<string, string> = {};
      try {
        const jwt = localStorage.getItem('cc_customer_token');
        if (jwt) headers.Authorization = `Bearer ${jwt}`;
      } catch {
        // ignore
      }

      let res: Response | null = null;
      // Backend order insert is async after payment — retry briefly on 404
      for (let attempt = 0; attempt < 5; attempt++) {
        res = await fetch(`${API_BASE}/orders/receipt?${params.toString()}`, { headers });
        if (res.status !== 404) break;
        await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
      }
      if (!res || !res.ok) {
        const json = res ? await res.json().catch(() => ({})) : {};
        throw new Error(json.message || `Receipt unavailable (${res?.status || 'network'})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `receipt-${order.orderNumber || 'order'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'Could not download receipt');
    } finally {
      setDownloading(false);
    }
  };

  const completedDate = new Date(order.completedAt).toLocaleString('en-CA', {
    timeZone: 'America/Vancouver',
  });

  return (
    <>
      <div className="card order-confirmation">
        <div className="order-confirmation-header">
          <div className="order-confirmation-icon">&#x2713;</div>
          <h2>Order Confirmed</h2>
          <p>Thank you! Your payment was received.</p>
          {order.orderNumber && (
            <p className="order-confirmation-meta">
              Order <strong>#{order.orderNumber}</strong>
            </p>
          )}
          <p className="order-confirmation-meta">{completedDate}</p>
          {(order.isTest || order.inventoryMode === 'simulated') && (
            <p
              className="order-confirmation-meta"
              style={{
                color: '#fff',
                background: '#7C2D12',
                fontWeight: 700,
                padding: '8px 12px',
                borderRadius: 6,
                marginTop: 12,
              }}
            >
              TEST ORDER — Sandbox / non-production payment. Not a real transaction.
              {order.inventoryMode === 'simulated' ? ' Inventory was not marked sold.' : ''}
            </p>
          )}
        </div>
      </div>

      <div className="card">
        <h2>Order Summary</h2>
        {(order.customerName || order.customerEmail) && (
          <div className="payment-review" style={{ marginBottom: 12 }}>
            {order.customerName && (
              <div className="payment-review-row">
                <span className="payment-review-label">Name</span>
                <span className="payment-review-value">{order.customerName}</span>
              </div>
            )}
            {order.customerEmail && (
              <div className="payment-review-row">
                <span className="payment-review-label">Email</span>
                <span className="payment-review-value">{order.customerEmail}</span>
              </div>
            )}
            <div className="payment-review-row">
              <span className="payment-review-label">Fulfillment</span>
              <span className="payment-review-value">
                {order.shippingMethod === 'pickup' ? 'In-Store Pickup' : 'Shipping'}
              </span>
            </div>
            {order.paymentId && (
              <div className="payment-review-row">
                <span className="payment-review-label">Payment</span>
                <span className="payment-review-value">{order.paymentId}</span>
              </div>
            )}
          </div>
        )}
        {order.items.map((item) => (
          <div className="cart-item" key={item.sku}>
            {item.photoUrl ? (
              <img
                className="cart-item-photo"
                src={item.photoUrl}
                alt={item.title}
              />
            ) : (
              <div className="cart-item-photo cart-item-photo-placeholder">
                &#x1F4F7;
              </div>
            )}
            <div className="cart-item-info">
              <span className="cart-item-title">{item.title || item.sku}</span>
              <div className="cart-item-meta">
                {[item.brand, item.size].filter(Boolean).join(' \u00B7 ')}
              </div>
              <div className="cart-item-meta">SKU: {item.sku}</div>
            </div>
            <div className="cart-item-price">${item.price.toFixed(2)}</div>
          </div>
        ))}
        <div className="cart-summary">
          <span>Subtotal</span>
          <span>${order.subtotal.toFixed(2)}</span>
        </div>
        {order.taxLines.map((tl, i) => (
          <div className="cart-summary" key={i}>
            <span>{tl.label}</span>
            <span>${tl.amount.toFixed(2)}</span>
          </div>
        ))}
        <div className="cart-summary">
          <span>
            {order.shippingMethod === 'pickup' ? 'In-Store Pickup' : 'Shipping'}
          </span>
          <span>
            {order.shippingCost === 0 ? 'Free' : `$${order.shippingCost.toFixed(2)}`}
          </span>
        </div>
        <div className="cart-summary total">
          <span>Total Paid</span>
          <span className="total-price">${order.total.toFixed(2)} CAD</span>
        </div>
      </div>

      <div className="card">
        <h2>Receipt</h2>
        <p className="contact-info-hint">
          A PDF receipt has also been emailed to you. You can download a copy here.
        </p>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleDownloadReceipt}
          disabled={downloading || (!order.receiptToken && !order.squareOrderId)}
        >
          {downloading ? 'Preparing PDF…' : 'Download Receipt (PDF)'}
        </button>
        {downloadError && <p className="coupon-error" style={{ marginTop: 8 }}>{downloadError}</p>}
      </div>

      <div className="card">
        <h2>What&apos;s Next</h2>
        {order.shippingMethod === 'pickup' ? (
          <div className="next-steps">
            <p>
              We&apos;ve set your items aside. You&apos;ll receive an email or
              text with pickup details shortly.
            </p>
            <p className="next-steps-detail">
              Pickup is typically available within 1 business day at our
              Victoria location.
            </p>
          </div>
        ) : (
          <div className="next-steps">
            <p>
              We&apos;ll pack your order and send you tracking information once
              it ships.
            </p>
          </div>
        )}
      </div>

      <button className="btn btn-secondary" onClick={onContinueShopping}>
        Continue Shopping
      </button>
    </>
  );
}
